import { useLayoutEffect, type RefObject } from "react";

/** Everything that decides where the textarea's text wraps. */
const TEXT_STYLES = [
  "font-family",
  "font-size",
  "font-weight",
  "font-style",
  "font-variant",
  "letter-spacing",
  "word-spacing",
  "line-height",
  "text-transform",
  "text-indent",
  "tab-size",
  "white-space",
  "word-break",
  "overflow-wrap",
  "padding-top",
  "padding-right",
  "padding-bottom",
  "padding-left",
] as const;

/**
 * Lays the mirror exactly over the textarea's text box and keeps it scrolled with it. Their shared
 * containing block must be the textarea's parent (make it `relative`), so nothing above that box
 * moves one without the other. Plain DOM writes from a ResizeObserver and the scroll event.
 */
export function useMirrorBox(
  input: RefObject<HTMLTextAreaElement | null>,
  mirror: RefObject<HTMLDivElement | null>
) {
  useLayoutEffect(() => {
    const element = input.current;
    const box = mirror.current;
    if (!element || !box) return;
    const place = () => {
      const style = getComputedStyle(element);
      for (const name of TEXT_STYLES) box.style.setProperty(name, style.getPropertyValue(name));
      box.style.top = `${element.offsetTop + element.clientTop}px`;
      box.style.left = `${element.offsetLeft + element.clientLeft}px`;
      // clientWidth leaves out a scrollbar, as the textarea's own text does.
      box.style.width = `${element.clientWidth}px`;
      box.style.height = `${element.clientHeight}px`;
      box.scrollTop = element.scrollTop;
    };
    const follow = () => {
      box.scrollTop = element.scrollTop;
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(element);
    // The attachment row above the text moves it without resizing it.
    if (element.parentElement) observer.observe(element.parentElement);
    element.addEventListener("scroll", follow, { passive: true });
    return () => {
      observer.disconnect();
      element.removeEventListener("scroll", follow);
    };
  }, [input, mirror]);
}
